import type { StateCreator } from "zustand" // move to lib/states
import { useMemo } from "react"
import { create } from "zustand" // move to lib/states
import { persist } from "zustand/middleware"

import type { FeedbackOrigin, FeedbackState, PainLevel } from "@productlane/db"
import type {
	ReplyStateFilter,
	ThreadFilterNegations,
} from "@productlane/zero/queries"
import { clientSafeThreadFilters } from "@productlane/zero/queries"

import { negationsAfterRemoval, withoutNegation } from "@/components/filters"

export type NumericOperator = ">=" | "<=" | "=" | "!="

export interface NumericFilter {
	operator: NumericOperator
	value: number
}

export type ThreadsFilterNegationKey = keyof ThreadFilterNegations

interface ThreadsFilterState {
	negations: ThreadFilterNegations
	setNegation: (key: ThreadsFilterNegationKey, isNegated: boolean) => void

	companyIds: Array<string | null>
	addCompany: (companyId: string | null) => void
	removeCompany: (companyId: string | null) => void
	clearCompanies: () => void

	personIds: Array<string | null>
	addPerson: (personId: string | null) => void
	removePerson: (personId: string | null) => void
	clearPersons: () => void

	issueIds: Array<string | null>
	addIssue: (issueId: string | null) => void
	removeIssue: (issueId: string | null) => void
	clearIssues: () => void

	tagIds: Array<string | null>
	addTag: (tagId: string | null) => void
	removeTag: (tagId: string | null) => void
	clearTags: () => void

	projectIds: Array<string | null>
	addProject: (projectId: string | null) => void
	removeProject: (projectId: string | null) => void
	clearProjects: () => void

	assigneeIds: Array<string | null>
	addAssignee: (userId: string | null) => void
	removeAssignee: (userId: string | null) => void
	clearAssignees: () => void

	segmentIds: Array<string>
	addSegment: (segmentId: string) => void
	removeSegment: (segmentId: string) => void
	clearSegments: () => void

	origins: Array<FeedbackOrigin>
	addOrigin: (origin: FeedbackOrigin) => void
	removeOrigin: (origin: FeedbackOrigin) => void
	clearOrigins: () => void

	portalInstanceIds: Array<string | null>
	addPortalInstance: (portalInstanceId: string | null) => void
	removePortalInstance: (portalInstanceId: string | null) => void
	clearPortalInstances: () => void

	slackWorkspaceIds: Array<string>
	addSlackWorkspace: (slackConnectSettingsId: string) => void
	removeSlackWorkspace: (slackConnectSettingsId: string) => void
	clearSlackWorkspaces: () => void

	slackChannelIds: Array<string>
	addSlackChannel: (channelId: string) => void
	removeSlackChannel: (channelId: string) => void
	clearSlackChannels: () => void

	emailAddressIds: Array<string | null>
	addEmailAddress: (emailAddressId: string | null) => void
	removeEmailAddress: (emailAddressId: string | null) => void
	clearEmailAddresses: () => void

	painLevels: Array<PainLevel>
	addPainLevel: (level: PainLevel) => void
	removePainLevel: (level: PainLevel) => void
	clearPainLevels: () => void

	creatorIds: Array<string>
	addCreator: (creatorId: string) => void
	removeCreator: (creatorId: string) => void
	clearCreators: () => void

	statuses: Array<FeedbackState>
	addStatus: (status: FeedbackState) => void
	removeStatus: (status: FeedbackState) => void
	clearStatuses: () => void

	replyStates: Array<ReplyStateFilter>
	addReplyState: (replyState: ReplyStateFilter) => void
	removeReplyState: (replyState: ReplyStateFilter) => void
	clearReplyStates: () => void

	companyTiers: Array<string>
	addCompanyTier: (tier: string) => void
	removeCompanyTier: (tier: string) => void
	clearCompanyTiers: () => void

	companyStatuses: Array<string>
	addCompanyStatus: (status: string) => void
	removeCompanyStatus: (status: string) => void
	clearCompanyStatuses: () => void

	companyOwnerIds: Array<string | null>
	addCompanyOwner: (ownerId: string | null) => void
	removeCompanyOwner: (ownerId: string | null) => void
	clearCompanyOwners: () => void

	issueStatuses: Array<string>
	addIssueStatus: (status: string) => void
	removeIssueStatus: (status: string) => void
	clearIssueStatuses: () => void

	companyRevenue: NumericFilter | null
	setCompanyRevenue: (filter: NumericFilter | null) => void

	companySize: NumericFilter | null
	setCompanySize: (filter: NumericFilter | null) => void

	reset: () => void
	loadFilters: (filters: {
		negations?: ThreadFilterNegations
		companyIds?: Array<string | null>
		personIds?: Array<string | null>
		issueIds?: Array<string | null>
		tagIds?: Array<string | null>
		projectIds?: Array<string | null>
		assigneeIds?: Array<string | null>
		origins?: Array<FeedbackOrigin | null>
		portalInstanceIds?: Array<string | null>
		slackWorkspaceIds?: Array<string>
		slackChannelIds?: Array<string>
		emailAddressIds?: Array<string | null>
		painLevels?: Array<PainLevel | null>
		creatorIds?: Array<string | null>
		statuses?: Array<FeedbackState | null>
		replyStates?: Array<ReplyStateFilter>
		companyTiers?: Array<string>
		companyStatuses?: Array<string>
		companyOwnerIds?: Array<string | null>
		issueStatuses?: Array<string>
		companyRevenue?: NumericFilter | null
		companySize?: NumericFilter | null
	}) => void
}

const filterDefinition: StateCreator<ThreadsFilterState> = (set) => ({
	negations: {},
	setNegation: (key, isNegated) => {
		set((prev) => ({
			negations: isNegated
				? { ...prev.negations, [key]: true }
				: withoutNegation(prev.negations, key),
		}))
	},

	companyIds: [],
	addCompany: (companyId) => {
		set((prev) => ({ companyIds: [...prev.companyIds, companyId] }))
	},
	removeCompany: (companyId) => {
		set((prev) => {
			const companyIds = prev.companyIds.filter((id) => id !== companyId)
			return {
				companyIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"companyIds",
					companyIds,
				),
			}
		})
	},
	clearCompanies: () => {
		set((prev) => ({
			companyIds: [],
			negations: withoutNegation(prev.negations, "companyIds"),
		}))
	},

	personIds: [],
	addPerson: (personId) => {
		set((prev) => ({ personIds: [...prev.personIds, personId] }))
	},
	removePerson: (personId) => {
		set((prev) => {
			const personIds = prev.personIds.filter((id) => id !== personId)
			return {
				personIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"personIds",
					personIds,
				),
			}
		})
	},
	clearPersons: () => {
		set((prev) => ({
			personIds: [],
			negations: withoutNegation(prev.negations, "personIds"),
		}))
	},

	issueIds: [],
	addIssue: (issueId) => {
		set((prev) => ({ issueIds: [...prev.issueIds, issueId] }))
	},
	removeIssue: (issueId) => {
		set((prev) => {
			const issueIds = prev.issueIds.filter((id) => id !== issueId)
			return {
				issueIds,
				negations: negationsAfterRemoval(prev.negations, "issueIds", issueIds),
			}
		})
	},
	clearIssues: () => {
		set((prev) => ({
			issueIds: [],
			negations: withoutNegation(prev.negations, "issueIds"),
		}))
	},

	tagIds: [],
	addTag: (tagId) => {
		set((prev) => ({ tagIds: [...prev.tagIds, tagId] }))
	},
	removeTag: (tagId) => {
		set((prev) => {
			const tagIds = prev.tagIds.filter((id) => id !== tagId)
			return {
				tagIds,
				negations: negationsAfterRemoval(prev.negations, "tagIds", tagIds),
			}
		})
	},
	clearTags: () => {
		set((prev) => ({
			tagIds: [],
			negations: withoutNegation(prev.negations, "tagIds"),
		}))
	},

	projectIds: [],
	addProject: (projectId) => {
		set((prev) => ({ projectIds: [...prev.projectIds, projectId] }))
	},
	removeProject: (projectId) => {
		set((prev) => {
			const projectIds = prev.projectIds.filter((id) => id !== projectId)
			return {
				projectIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"projectIds",
					projectIds,
				),
			}
		})
	},
	clearProjects: () => {
		set((prev) => ({
			projectIds: [],
			negations: withoutNegation(prev.negations, "projectIds"),
		}))
	},

	assigneeIds: [],
	addAssignee: (assigneeId) => {
		set((prev) => ({ assigneeIds: [...prev.assigneeIds, assigneeId] }))
	},
	removeAssignee: (assigneeId) => {
		set((prev) => {
			const assigneeIds = prev.assigneeIds.filter((id) => id !== assigneeId)
			return {
				assigneeIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"assigneeIds",
					assigneeIds,
				),
			}
		})
	},
	clearAssignees: () => {
		set((prev) => ({
			assigneeIds: [],
			negations: withoutNegation(prev.negations, "assigneeIds"),
		}))
	},

	segmentIds: [],
	addSegment: (segmentId) => {
		set((prev) => ({ segmentIds: [...prev.segmentIds, segmentId] }))
	},
	removeSegment: (segmentId) => {
		set((prev) => ({
			segmentIds: prev.segmentIds.filter((id) => id !== segmentId),
		}))
	},
	clearSegments: () => {
		set({ segmentIds: [] })
	},

	origins: [],
	addOrigin: (origin) => {
		set((prev) => ({ origins: [...prev.origins, origin] }))
	},
	removeOrigin: (origin) => {
		set((prev) => {
			const origins = prev.origins.filter((id) => id !== origin)
			return {
				origins,
				negations: negationsAfterRemoval(prev.negations, "origins", origins),
			}
		})
	},
	clearOrigins: () => {
		set((prev) => ({
			origins: [],
			negations: withoutNegation(prev.negations, "origins"),
		}))
	},

	portalInstanceIds: [],
	addPortalInstance: (portalInstanceId) => {
		set((prev) => ({
			portalInstanceIds: [...prev.portalInstanceIds, portalInstanceId],
		}))
	},
	removePortalInstance: (portalInstanceId) => {
		set((prev) => {
			const portalInstanceIds = prev.portalInstanceIds.filter(
				(id) => id !== portalInstanceId,
			)
			return {
				portalInstanceIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"portalInstanceIds",
					portalInstanceIds,
				),
			}
		})
	},
	clearPortalInstances: () => {
		set((prev) => ({
			portalInstanceIds: [],
			negations: withoutNegation(prev.negations, "portalInstanceIds"),
		}))
	},

	slackWorkspaceIds: [],
	addSlackWorkspace: (slackConnectSettingsId) => {
		set((prev) => ({
			slackWorkspaceIds: [...prev.slackWorkspaceIds, slackConnectSettingsId],
		}))
	},
	removeSlackWorkspace: (slackConnectSettingsId) => {
		set((prev) => {
			const slackWorkspaceIds = prev.slackWorkspaceIds.filter(
				(id) => id !== slackConnectSettingsId,
			)
			return {
				slackWorkspaceIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"slackWorkspaceIds",
					slackWorkspaceIds,
				),
			}
		})
	},
	clearSlackWorkspaces: () => {
		set((prev) => ({
			slackWorkspaceIds: [],
			negations: withoutNegation(prev.negations, "slackWorkspaceIds"),
		}))
	},

	slackChannelIds: [],
	addSlackChannel: (channelId) => {
		set((prev) => ({
			slackChannelIds: [...prev.slackChannelIds, channelId],
		}))
	},
	removeSlackChannel: (channelId) => {
		set((prev) => {
			const slackChannelIds = prev.slackChannelIds.filter(
				(id) => id !== channelId,
			)
			return {
				slackChannelIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"slackChannelIds",
					slackChannelIds,
				),
			}
		})
	},
	clearSlackChannels: () => {
		set((prev) => ({
			slackChannelIds: [],
			negations: withoutNegation(prev.negations, "slackChannelIds"),
		}))
	},

	emailAddressIds: [],
	addEmailAddress: (emailAddressId) => {
		set((prev) => ({
			emailAddressIds: [...prev.emailAddressIds, emailAddressId],
		}))
	},
	removeEmailAddress: (emailAddressId) => {
		set((prev) => {
			const emailAddressIds = prev.emailAddressIds.filter(
				(id) => id !== emailAddressId,
			)
			return {
				emailAddressIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"emailAddressIds",
					emailAddressIds,
				),
			}
		})
	},
	clearEmailAddresses: () => {
		set((prev) => ({
			emailAddressIds: [],
			negations: withoutNegation(prev.negations, "emailAddressIds"),
		}))
	},

	painLevels: [],
	addPainLevel: (painLevel) => {
		set((prev) => ({ painLevels: [...prev.painLevels, painLevel] }))
	},
	removePainLevel: (painLevel) => {
		set((prev) => {
			const painLevels = prev.painLevels.filter((id) => id !== painLevel)
			return {
				painLevels,
				negations: negationsAfterRemoval(
					prev.negations,
					"painLevels",
					painLevels,
				),
			}
		})
	},
	clearPainLevels: () => {
		set((prev) => ({
			painLevels: [],
			negations: withoutNegation(prev.negations, "painLevels"),
		}))
	},

	creatorIds: [],
	addCreator: (creatorId) => {
		set((prev) => ({ creatorIds: [...prev.creatorIds, creatorId] }))
	},
	removeCreator: (creatorId) => {
		set((prev) => {
			const creatorIds = prev.creatorIds.filter((id) => id !== creatorId)
			return {
				creatorIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"creatorIds",
					creatorIds,
				),
			}
		})
	},
	clearCreators: () => {
		set((prev) => ({
			creatorIds: [],
			negations: withoutNegation(prev.negations, "creatorIds"),
		}))
	},

	statuses: [],
	addStatus: (status) => {
		set((prev) => ({ statuses: [...prev.statuses, status] }))
	},
	removeStatus: (status) => {
		set((prev) => {
			const statuses = prev.statuses.filter((s) => s !== status)
			return {
				statuses,
				negations: negationsAfterRemoval(prev.negations, "statuses", statuses),
			}
		})
	},
	clearStatuses: () => {
		set((prev) => ({
			statuses: [],
			negations: withoutNegation(prev.negations, "statuses"),
		}))
	},

	replyStates: [],
	addReplyState: (replyState) => {
		set((prev) => ({ replyStates: [...prev.replyStates, replyState] }))
	},
	removeReplyState: (replyState) => {
		set((prev) => {
			const replyStates = prev.replyStates.filter((s) => s !== replyState)
			return {
				replyStates,
				negations: negationsAfterRemoval(
					prev.negations,
					"replyStates",
					replyStates,
				),
			}
		})
	},
	clearReplyStates: () => {
		set((prev) => ({
			replyStates: [],
			negations: withoutNegation(prev.negations, "replyStates"),
		}))
	},

	companyTiers: [],
	addCompanyTier: (tier) => {
		set((prev) => ({ companyTiers: [...prev.companyTiers, tier] }))
	},
	removeCompanyTier: (tier) => {
		set((prev) => {
			const companyTiers = prev.companyTiers.filter((t) => t !== tier)
			return {
				companyTiers,
				negations: negationsAfterRemoval(
					prev.negations,
					"companyTiers",
					companyTiers,
				),
			}
		})
	},
	clearCompanyTiers: () => {
		set((prev) => ({
			companyTiers: [],
			negations: withoutNegation(prev.negations, "companyTiers"),
		}))
	},

	companyStatuses: [],
	addCompanyStatus: (status) => {
		set((prev) => ({ companyStatuses: [...prev.companyStatuses, status] }))
	},
	removeCompanyStatus: (status) => {
		set((prev) => {
			const companyStatuses = prev.companyStatuses.filter((s) => s !== status)
			return {
				companyStatuses,
				negations: negationsAfterRemoval(
					prev.negations,
					"companyStatuses",
					companyStatuses,
				),
			}
		})
	},
	clearCompanyStatuses: () => {
		set((prev) => ({
			companyStatuses: [],
			negations: withoutNegation(prev.negations, "companyStatuses"),
		}))
	},

	companyOwnerIds: [],
	addCompanyOwner: (ownerId) => {
		set((prev) => ({ companyOwnerIds: [...prev.companyOwnerIds, ownerId] }))
	},
	removeCompanyOwner: (ownerId) => {
		set((prev) => {
			const companyOwnerIds = prev.companyOwnerIds.filter(
				(id) => id !== ownerId,
			)
			return {
				companyOwnerIds,
				negations: negationsAfterRemoval(
					prev.negations,
					"companyOwnerIds",
					companyOwnerIds,
				),
			}
		})
	},
	clearCompanyOwners: () => {
		set((prev) => ({
			companyOwnerIds: [],
			negations: withoutNegation(prev.negations, "companyOwnerIds"),
		}))
	},

	issueStatuses: [],
	addIssueStatus: (status) => {
		set((prev) => ({ issueStatuses: [...prev.issueStatuses, status] }))
	},
	removeIssueStatus: (status) => {
		set((prev) => ({
			issueStatuses: prev.issueStatuses.filter((s) => s !== status),
		}))
	},
	clearIssueStatuses: () => {
		set({ issueStatuses: [] })
	},

	companyRevenue: null,
	setCompanyRevenue: (filter) => {
		set({ companyRevenue: filter })
	},

	companySize: null,
	setCompanySize: (filter) => {
		set({ companySize: filter })
	},

	reset: () => {
		set({
			negations: {},
			segmentIds: [],
			origins: [],
			portalInstanceIds: [],
			slackWorkspaceIds: [],
			slackChannelIds: [],
			emailAddressIds: [],
			painLevels: [],
			creatorIds: [],
			assigneeIds: [],
			projectIds: [],
			tagIds: [],
			issueIds: [],
			companyIds: [],
			personIds: [],
			statuses: [],
			replyStates: [],
			companyTiers: [],
			companyStatuses: [],
			companyOwnerIds: [],
			issueStatuses: [],
			companyRevenue: null,
			companySize: null,
		})
	},
	loadFilters: (rawFilters) => {
		const filters = clientSafeThreadFilters(rawFilters)
		set({
			negations: filters.negations ?? {},
			companyIds: filters.companyIds ?? [],
			personIds: filters.personIds ?? [],
			issueIds: filters.issueIds ?? [],
			tagIds: filters.tagIds ?? [],
			projectIds: filters.projectIds ?? [],
			assigneeIds: filters.assigneeIds ?? [],
			origins: (filters.origins ?? []).filter(
				(v): v is FeedbackOrigin => v !== null,
			),
			portalInstanceIds: filters.portalInstanceIds ?? [],
			slackWorkspaceIds: filters.slackWorkspaceIds ?? [],
			slackChannelIds: filters.slackChannelIds ?? [],
			emailAddressIds: filters.emailAddressIds ?? [],
			painLevels: (filters.painLevels ?? []).filter(
				(v): v is PainLevel => v !== null,
			),
			creatorIds: (filters.creatorIds ?? []).filter(
				(v): v is string => v !== null,
			),
			statuses: (filters.statuses ?? []).filter(
				(v): v is FeedbackState => v !== null,
			),
			replyStates: filters.replyStates ?? [],
			companyTiers: filters.companyTiers ?? [],
			companyStatuses: filters.companyStatuses ?? [],
			companyOwnerIds: filters.companyOwnerIds ?? [],
			issueStatuses: filters.issueStatuses ?? [],
			companyRevenue: filters.companyRevenue ?? null,
			companySize: filters.companySize ?? null,
		})
	},
})

export const useThreadsFilterState = create<ThreadsFilterState>()(
	persist(filterDefinition, {
		name: "threads-filter",
		version: 1,
		migrate: (persisted) =>
			clientSafeThreadFilters(persisted as ThreadsFilterState),
	}),
)

export function useIsThreadFilterActive() {
	const {
		creatorIds,
		origins,
		portalInstanceIds,
		slackWorkspaceIds,
		slackChannelIds,
		emailAddressIds,
		painLevels,
		segmentIds,
		assigneeIds,
		projectIds,
		tagIds,
		issueIds,
		companyIds,
		personIds,
		statuses,
		replyStates,
		companyTiers,
		companyStatuses,
		companyOwnerIds,
		issueStatuses,
		companyRevenue,
		companySize,
	} = useThreadsFilterState()
	const isFilterActive = useMemo(() => {
		return (
			creatorIds.length > 0 ||
			origins.length > 0 ||
			portalInstanceIds.length > 0 ||
			slackWorkspaceIds.length > 0 ||
			slackChannelIds.length > 0 ||
			emailAddressIds.length > 0 ||
			painLevels.length > 0 ||
			segmentIds.length > 0 ||
			assigneeIds.length > 0 ||
			projectIds.length > 0 ||
			tagIds.length > 0 ||
			issueIds.length > 0 ||
			companyIds.length > 0 ||
			personIds.length > 0 ||
			statuses.length > 0 ||
			replyStates.length > 0 ||
			companyTiers.length > 0 ||
			companyStatuses.length > 0 ||
			companyOwnerIds.length > 0 ||
			issueStatuses.length > 0 ||
			companyRevenue !== null ||
			companySize !== null
		)
	}, [
		creatorIds.length,
		origins.length,
		portalInstanceIds.length,
		slackWorkspaceIds.length,
		slackChannelIds.length,
		emailAddressIds.length,
		painLevels.length,
		segmentIds.length,
		assigneeIds.length,
		projectIds.length,
		tagIds.length,
		issueIds.length,
		companyIds.length,
		personIds.length,
		statuses.length,
		replyStates.length,
		companyTiers.length,
		companyStatuses.length,
		companyOwnerIds.length,
		issueStatuses.length,
		companyRevenue,
		companySize,
	])
	return isFilterActive
}
